import Cookies from 'js-cookie';
import isEqual from 'lodash/isEqual';
import { useCallback, useEffect } from 'react';
import { useIntercom, type IntercomProps } from 'react-use-intercom';

// Intercom names every cookie it sets on our domain with this prefix, so the
// prefix is the contract rather than the three names we happen to know
// (`intercom-{id,session,device-id}-<appId>`).
const INTERCOM_COOKIE_PREFIX = 'intercom-';

/**
 * Fully reboots Intercom so audience-gated content is evaluated from a fresh
 * `apiBoot` response instead of a same-session `apiUpdate` ping.
 *
 * Intercom can keep the messenger connected from cookies across SPA
 * navigation. In that state, `update()` sends the new attributes but does not
 * reliably re-evaluate tooltips/outbound content whose audience rules depend
 * on page-level attributes like program or state.
 */
export function useIntercomReboot() {
  const { boot, hardShutdown } = useIntercom();

  return useCallback(
    (appId: string, userData: IntercomProps) => {
      // `hardShutdown()` deletes the SDK's global state, so the loader stub has
      // to be recreated before calling `boot()`.
      hardShutdown();
      tearDownIntercomWidget();
      bootstrapIntercomLoader(appId);
      boot(userData);
    },
    [boot, hardShutdown],
  );
}

export type IntercomCustomAttributes = NonNullable<IntercomProps['customAttributes']>;

export type IntercomAudienceAttributes = {
  userCustomAttributes: IntercomCustomAttributes;
  companyCustomAttributes?: IntercomCustomAttributes;
};

/**
 * Compares the full set of custom attributes that can affect Intercom audience
 * rules, instead of maintaining a second hardcoded list of reboot triggers.
 */
export function haveIntercomAudienceAttributesChanged(
  previous: IntercomAudienceAttributes | null,
  next: IntercomAudienceAttributes,
) {
  return previous !== null && !isEqual(previous, next);
}

/**
 * Keeps public routes from inheriting a stale authenticated Intercom session
 * when a user navigates from the internal app to a customer-facing page in the
 * same browser session.
 */
export function useDisableIntercomWidget() {
  const { hardShutdown } = useIntercom();

  useEffect(() => disableIntercomWidgetWithRetry(hardShutdown), [hardShutdown]);
}

/**
 * Disables Intercom immediately, then repeats DOM cleanup shortly after. Both
 * the route wrapper and invite-token branch use this helper so every public
 * path has the same protection against the CDN inserting frames after shutdown.
 */
export function disableIntercomWidgetWithRetry(hardShutdown?: () => void) {
  hardShutdown?.();
  disableIntercomWidget();

  // The Intercom script can recreate iframes asynchronously after shutdown.
  // A short retry window catches those late insertions without keeping a
  // background observer alive for the lifetime of the public route.
  const cleanupTimers = [window.setTimeout(disableIntercomWidget, 0), window.setTimeout(disableIntercomWidget, 500)];
  return () => cleanupTimers.forEach(window.clearTimeout);
}

/**
 * Ends the Intercom session on logout: shuts the messenger down, drops the
 * globals and widget DOM, then removes the cookies Intercom leaves behind.
 *
 * Deliberately separate from `disableIntercomWidget`, which public routes use to
 * hide the widget without destroying identity — a route change should not cost a
 * user their Intercom session. Logout is the only place where that is the intent.
 *
 * Shutdown runs first so Intercom can still read its own session while ending
 * it; the cookie sweep afterwards removes what shutdown leaves behind.
 *
 * Every step is synchronous, so callers can rely on this completing before a
 * `window.location.assign` they dispatch next. The retrying variant is
 * deliberately not used for logout: its deferred cleanup passes cannot survive
 * a navigation, so calling it would imply protection it could not deliver.
 */
export function endIntercomSession() {
  disableIntercomWidget();
  clearIntercomCookies();
}

/**
 * Removes both the browser globals and the rendered widget artifacts. This is
 * intentionally stronger than Intercom's plain `shutdown` because public routes
 * should not leave a hidden messenger frame or stale settings behind.
 */
export function disableIntercomWidget() {
  const w = window as unknown as {
    Intercom?: (...args: unknown[]) => void;
    intercomSettings?: Record<string, unknown>;
  };

  w.Intercom?.('shutdown');
  delete w.Intercom;
  delete w.intercomSettings;
  tearDownIntercomWidget();
}

/**
 * Removes DOM nodes created by the Intercom loader. Intercom can render under
 * different container ids and script URLs, so cleanup checks all variants we
 * have observed locally and in the hosted widget.
 */
export function tearDownIntercomWidget() {
  for (const id of ['intercom-frame', 'intercom-container', 'intercom-modal-container', 'intercom-css-container']) {
    document.getElementById(id)?.remove();
  }

  document
    .querySelectorAll(
      [
        'iframe[name*="intercom"]',
        'iframe[src*="intercom"]',
        'script[src*="widget.intercom.io/widget/"]',
        'script[src*="js.intercomcdn.com"]',
      ].join(','),
    )
    .forEach((el) => el.remove());
}

/**
 * Deletes every `intercom-` prefixed cookie. Intercom documents that
 * `Intercom('shutdown')` alone does not remove them all — the `intercom-id-`
 * cookie holding the anonymous identifier survives, and that leftover is what
 * lets the previous user's conversation history surface for the next user and
 * their anonymous lead be merged into the next authenticated contact record.
 *
 * A cookie is only deleted when name, domain and path all match, and Intercom
 * sets these client-side with attributes we cannot read back. So each name is
 * cleared host-only and again for every parent domain a `Set-Cookie` from this
 * host could legally have named; a single host-only attempt would silently
 * no-op against a domain-scoped cookie.
 */
export function clearIntercomCookies() {
  const domains = cookieDomainCandidates(window.location.hostname);
  const intercomCookies = Object.keys(Cookies.get()).filter((cookie) => cookie.startsWith(INTERCOM_COOKIE_PREFIX));

  for (const name of intercomCookies) {
    Cookies.remove(name, { path: '/' });
    for (const domain of domains) {
      Cookies.remove(name, { path: '/', domain });
    }
  }
}

/**
 * Every domain a cookie visible to this host could have been scoped to, most
 * specific first: `a.b.example.com` yields `a.b.example.com`, `b.example.com`,
 * `example.com`. Stops before the final label (the TLD), not at a public
 * suffix boundary — for a multi-part TLD like `co.uk` this would still emit
 * it. Harmless here: our domains are `palmetto.finance` and `localhost`,
 * both single-part TLDs, and browsers reject cookie writes scoped to a public
 * suffix regardless. Yields nothing for hosts that cannot carry a domain
 * attribute at all — IP literals and single-label hosts like `localhost`,
 * where the host-only clear is the only form the browser will accept.
 */
export function cookieDomainCandidates(hostname: string): string[] {
  if (!hostname.includes('.') || hostname.startsWith('[') || /^[\d.]+$/.test(hostname)) {
    return [];
  }

  const labels = hostname.split('.');
  return labels.slice(0, labels.length - 1).map((_, index) => labels.slice(index).join('.'));
}

/**
 * Recreates the minimal Intercom bootloader stub after `hardShutdown()` deletes
 * `window.Intercom`. This mirrors Intercom's own snippet closely enough for
 * react-use-intercom's `boot()` call to enqueue against the fresh loader.
 */
function bootstrapIntercomLoader(appId: string) {
  type IntercomStub = ((...args: unknown[]) => void) & {
    q: unknown[][];
    c: (args: unknown[]) => void;
  };

  const w = window as unknown as {
    Intercom: IntercomStub;
    intercomSettings: Record<string, unknown>;
  };

  w.intercomSettings = { app_id: appId };
  const stub = function (...args: unknown[]) {
    stub.c(args);
  } as IntercomStub;
  stub.q = [];
  stub.c = function (args: unknown[]) {
    stub.q.push(args);
  };
  w.Intercom = stub;

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `https://widget.intercom.io/widget/${appId}`;
  document.head.appendChild(script);
}
